/**
 * Utility functions for labor unit calculations
 */

import { LaborUnitSpec, CalculatedLaborUnit, PresetConfig } from '../types/laborUnit.types';

/**
 * Default labor unit configuration
 */
export const DEFAULT_LABOR_CONFIG: PresetConfig = {
  name: 'Standard Labor Units',
  description: 'Standard labor unit configuration without supervision',
  layoutPct: 15,
  materialPct: 10,
  nonProductivePct: 5,
  toolHandlingPct: 10,
};

/**
 * Validates that individual percentages are reasonable
 */
export function validatePercentages(
  layoutPct: number,
  materialPct: number,
  nonProductivePct: number,
  toolHandlingPct: number
): { isValid: boolean; total: number; message?: string } {
  // Each percentage should be reasonable on its own
  const percentages = [layoutPct, materialPct, nonProductivePct, toolHandlingPct];
  const isValid = percentages.every(pct => pct >= 0 && pct <= 100);

  // We still calculate total but don't use it for validation
  const total = percentages.reduce((sum, pct) => sum + pct, 0);

  return {
    isValid,
    total,
    message: isValid
      ? undefined
      : `Each percentage should be between 0% and 100% of installation time`,
  };
}

/**
 * Calculates the full labor unit breakdown from the specification
 */
export function calculateLaborUnit(spec: LaborUnitSpec): CalculatedLaborUnit {
  const {
    installationHours,
    layoutPct = DEFAULT_LABOR_CONFIG.layoutPct,
    materialPct = DEFAULT_LABOR_CONFIG.materialPct,
    nonProductivePct = DEFAULT_LABOR_CONFIG.nonProductivePct,
    toolHandlingPct = DEFAULT_LABOR_CONFIG.toolHandlingPct,
  } = spec;

  // Calculate support hours based on percentages of installation time
  const toHours = (pct: number) => (installationHours * pct) / 100;

  const calculated = {
    installation: installationHours,
    layout: toHours(layoutPct),
    material: toHours(materialPct),
    nonProductive: toHours(nonProductivePct),
    supervision: 0, // Always 0 since we don't include supervision
    toolHandling: toHours(toolHandlingPct),
    total: 0,
  };

  // Total is the sum of all components
  calculated.total = Object.values(calculated).reduce(
    (sum, val) => (typeof val === 'number' ? sum + val : sum),
    0
  );

  return calculated;
}

/**
 * Formats hours to a readable string with precision to handle minutes
 * Displays up to 3 decimal places (approximately to the minute)
 */
export function formatHours(hours: number): string {
  return `${hours.toFixed(3)} hrs`;
}

/**
 * Re-scales a single percentage to be within valid bounds
 */
export function rescalePercentages(
  changedField: keyof Omit<PresetConfig, 'name' | 'description'>,
  newValue: number,
  currentValues: Omit<PresetConfig, 'name' | 'description'>
): Omit<PresetConfig, 'name' | 'description'> {
  // Simply ensure the new value is between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, newValue));
  return {
    ...currentValues,
    [changedField]: clampedValue,
  };
}

/**
 * Analyzes item characteristics to determine labor unit adjustments
 */
export interface ItemCharacteristics {
  itemName: string;
  attributes: {
    name: string;
    value: string;
  }[];
  description?: string;
}

export interface LaborAdjustment {
  factor: number;
  reason: string;
}

/**
 * Analyzes a group of items to determine if they are similar enough for bulk labor unit application
 * Returns groups of items that should have similar labor units
 */
export function analyzeItemSimilarity(items: ItemCharacteristics[]): {
  groups: ItemCharacteristics[][];
  explanation: string;
} {
  // This is a placeholder for the AI analysis
  // In the real implementation, this would use the web_search tool or a custom AI endpoint
  // to analyze item characteristics and group them by similarity

  const groups: ItemCharacteristics[][] = [];
  let currentGroup: ItemCharacteristics[] = [];
  let explanation = '';

  // Example grouping logic for wire gauge
  items.forEach(item => {
    const gaugeMatch = item.itemName.match(/(\d+)\s*(?:AWG|gauge)/i);
    if (gaugeMatch) {
      const gauge = parseInt(gaugeMatch[1]);
      // Group similar gauges together (e.g., 10-12, 14-16)
      const groupIndex = Math.floor(gauge / 2);
      if (!groups[groupIndex]) {
        groups[groupIndex] = [];
      }
      groups[groupIndex].push(item);
    } else {
      currentGroup.push(item);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  // Build explanation
  if (groups.length > 1) {
    explanation =
      'Items have been grouped by wire gauge for more accurate labor unit calculation. ' +
      'Larger gauge wire typically requires more handling time and physical effort.';
  } else {
    explanation =
      'All items appear to have similar characteristics and can use the same labor unit calculation.';
  }

  return { groups, explanation };
}

/**
 * Analyzes item characteristics to determine labor unit adjustments
 */
export function calculateLaborAdjustments(item: ItemCharacteristics): LaborAdjustment {
  let factor = 1.0;
  let reasons: string[] = [];

  // Example adjustment logic for wire gauge
  const gaugeMatch = item.itemName.match(/(\d+)\s*(?:AWG|gauge)/i);
  if (gaugeMatch) {
    const gauge = parseInt(gaugeMatch[1]);
    if (gauge <= 10) {
      factor *= 1.3;
      reasons.push('Heavy gauge wire (≤10 AWG) requires more physical effort and handling time');
    } else if (gauge <= 12) {
      factor *= 1.15;
      reasons.push('Medium gauge wire (12 AWG) requires moderate additional effort');
    }
  }

  // Example adjustment for conduit size
  const conduitMatch = item.itemName.match(/(\d+)\s*(?:inch|")\s*conduit/i);
  if (conduitMatch) {
    const size = parseInt(conduitMatch[1]);
    if (size >= 2) {
      factor *= 1.25;
      reasons.push('Large conduit (≥2") requires additional handling time and tools');
    }
  }

  return {
    factor,
    reason: reasons.join('. ') || 'No adjustments needed for standard installation',
  };
}

/**
 * Applies labor adjustments to a calculated labor unit
 */
export function applyLaborAdjustments(
  laborUnit: CalculatedLaborUnit,
  adjustment: LaborAdjustment
): CalculatedLaborUnit {
  return {
    installation: laborUnit.installation * adjustment.factor,
    layout: laborUnit.layout * adjustment.factor,
    material: laborUnit.material * adjustment.factor,
    nonProductive: laborUnit.nonProductive * adjustment.factor,
    toolHandling: laborUnit.toolHandling * adjustment.factor,
    total: laborUnit.total * adjustment.factor,
  };
}
